import type { FlowData, FiveTuple } from '@/types';
import { DEMO_CITIES, THREAT_NAMES } from '@/config';
import { store } from '@/state';
import { addArc } from '@/visualization/arcs';
import { addEmber } from '@/visualization/embers';
import { addEventToFeed } from '@/ui/events';

/** Demo flow interval in milliseconds */
const FLOW_INTERVAL = 250;

/** Info event interval in milliseconds */
const INFO_EVENT_INTERVAL = 15000;

/** Demo mode interval IDs */
let flowIntervalId: number | null = null;
let infoEventIntervalId: number | null = null;

/** Common protocols for demo */
const PROTOCOLS = ['TCP', 'UDP', 'TCP', 'TCP', 'UDP', 'ICMP'];

/** Common ports for demo */
const COMMON_PORTS = [80, 443, 8080, 22, 53, 3389, 8443, 993, 587, 3306, 5432, 6379, 27017];

/**
 * Generate a random IP address
 */
function randomIp(): string {
  return `${10 + Math.floor(Math.random() * 240)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${1 + Math.floor(Math.random() * 254)}`;
}

/**
 * Generate a random 5-tuple
 */
function generateFiveTuple(): FiveTuple {
  return {
    srcIp: randomIp(),
    dstIp: randomIp(),
    srcPort: 1024 + Math.floor(Math.random() * 64511),
    dstPort: COMMON_PORTS[Math.floor(Math.random() * COMMON_PORTS.length)],
    protocol: PROTOCOLS[Math.floor(Math.random() * PROTOCOLS.length)],
  };
}

/**
 * Start demo mode with simulated flows
 */
export function startDemoMode(): void {
  console.log('Starting demo mode...');

  addEventToFeed({
    severity: 'warn',
    source: 'System',
    message: 'Demo mode activated - no WebSocket connection',
  });

  // Generate demo flows
  flowIntervalId = window.setInterval(generateDemoFlow, FLOW_INTERVAL);

  // Generate periodic info events
  infoEventIntervalId = window.setInterval(generateInfoEvent, INFO_EVENT_INTERVAL);
}

/**
 * Stop demo mode
 */
export function stopDemoMode(): void {
  if (flowIntervalId !== null) {
    clearInterval(flowIntervalId);
    flowIntervalId = null;
  }

  if (infoEventIntervalId !== null) {
    clearInterval(infoEventIntervalId);
    infoEventIntervalId = null;
  }
}

/**
 * Check if demo mode is active
 * @returns True if demo mode is running
 */
export function isDemoModeActive(): boolean {
  return flowIntervalId !== null;
}

/** Las Vegas - all traffic origin */
const LAS_VEGAS = DEMO_CITIES.find(c => c.name === 'Las Vegas')!;

/** Destination cities (everything except Las Vegas) */
const DESTINATION_CITIES = DEMO_CITIES.filter(c => c.name !== 'Las Vegas');

/**
 * Generate a demo flow from Las Vegas out to a random city
 */
function generateDemoFlow(): void {
  const src = LAS_VEGAS;
  const dst = DESTINATION_CITIES[Math.floor(Math.random() * DESTINATION_CITIES.length)];

  const threat = Math.floor(Math.random() * 6);
  const flow: FlowData = {
    srcLat: src.lat,
    srcLng: src.lng,
    dstLat: dst.lat,
    dstLng: dst.lng,
    mbps: Math.random() * 200,
    threat,
  };

  // Update stats
  store.incrementFlows();

  // Add visualization
  addArc(flow);
  addEmber(flow);

  // Add all flows to the event feed with 5-tuple info
  if (Math.random() < 0.15) {
    const fiveTuple = generateFiveTuple();
    const severity = threat >= 5 ? 'critical' : threat >= 4 ? 'error' : threat >= 3 ? 'warn' : 'info';

    addEventToFeed({
      severity,
      source: `${src.name} \u2192 ${dst.name}`,
      message: threat >= 3
        ? `${THREAT_NAMES[threat as keyof typeof THREAT_NAMES]} threat flow detected`
        : 'Flow observed',
      fiveTuple,
      mbps: flow.mbps,
      threat,
    });
  }
}

/**
 * Generate a periodic info event
 */
function generateInfoEvent(): void {
  const messages = [
    { source: 'Monitor', message: 'Network health check completed', severity: 'info' as const },
    { source: 'Scanner', message: 'Periodic scan initiated', severity: 'info' as const },
    { source: 'Firewall', message: 'Rule update applied', severity: 'info' as const },
  ];

  const msg = messages[Math.floor(Math.random() * messages.length)];
  addEventToFeed(msg);
}
